import React, { createContext, useContext, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';

const InfoModalContext = createContext(null);

export const InfoModalProvider = ({ children }) => {
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    showCancel: true,
    onConfirm: null,
    onCancel: null,
  });

  const showInfoModal = (options) => {
    setModalState({
      isOpen: true,
      title: options.title || 'Information',
      message: options.message || '',
      confirmLabel: options.confirmLabel || 'Confirm',
      cancelLabel: options.cancelLabel || 'Cancel',
      showCancel: options.showCancel !== undefined ? options.showCancel : true,
      onConfirm: options.onConfirm || null,
      onCancel: options.onCancel || null,
    });
  };

  const hideInfoModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <InfoModalContext.Provider value={{ showInfoModal, hideInfoModal }}>
      {children}
      <Dialog
        open={modalState.isOpen}
        onClose={hideInfoModal}
        PaperProps={{
          sx: {
            backgroundColor: '#23272f', // Premium dark paper background
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            p: 3,
            maxWidth: '440px',
            width: '100%',
            color: '#fff',
            position: 'relative',
          },
        }}
      >
        <IconButton
          onClick={hideInfoModal}
          aria-label="close info modal"
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'rgba(255, 255, 255, 0.4)',
            '&:hover': {
              color: '#fff',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            mt: 2,
            mb: 1,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 52, color: '#3b82f6', mb: 2 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#fff',
              mb: 1.5,
              fontSize: '1.25rem',
            }}
          >
            {modalState.title}
          </Typography>
        </Box>

        <DialogContent sx={{ p: 0, textAlign: 'center', mb: 4 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#b0b3b8',
              lineHeight: 1.6,
              fontSize: '0.95rem',
              whiteSpace: 'pre-line',
            }}
          >
            {modalState.message}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', p: 0, gap: 2 }}>
          {modalState.showCancel && (
            <Button
              onClick={() => {
                if (modalState.onCancel) {
                  modalState.onCancel();
                }
                hideInfoModal();
              }}
              variant="outlined"
              sx={{
                color: '#b0b3b8',
                borderColor: 'rgba(255, 255, 255, 0.12)',
                textTransform: 'none',
                px: 4,
                py: 1,
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '0.9rem',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              {modalState.cancelLabel}
            </Button>
          )}
          <Button
            onClick={() => {
              if (modalState.onConfirm) {
                modalState.onConfirm();
              }
              hideInfoModal();
            }}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.9rem',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
              backgroundColor: '#3b82f6',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#2563eb',
                boxShadow: '0 6px 16px rgba(59, 130, 246, 0.35)',
              },
            }}
          >
            {modalState.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </InfoModalContext.Provider>
  );
};

export const useInfoModal = () => {
  const context = useContext(InfoModalContext);
  if (!context) {
    throw new Error('useInfoModal must be used within an InfoModalProvider');
  }
  return context;
};
